import { useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import Logo from "../Logo/Logo";
import { treatmentNavLinks } from "../../routes/Services/serviceData";
import { BOOKING_URL, trackBookingClick, trackNavClick } from "../../utils/tracking";

const navLinks = [
  { to: "/", label: "Home" },
  {
    label: "Treatments",
    children: treatmentNavLinks,
  },
  { to: "/aftercare", label: "Aftercare" },
  { to: "/packages", label: "Packages" },
  { to: "/reviews", label: "Reviews" },
  { to: "/gallery", label: "Gallery" },
  { to: "/medik8", label: "Medik8" },
  {
    to: BOOKING_URL,
    label: "Book Now",
    isCta: true,
  },
];

const NavBar = () => {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [treatmentsAnchor, setTreatmentsAnchor] = useState(null);
  const [treatmentsOpenMobile, setTreatmentsOpenMobile] = useState(false);
  const treatmentsMenuOpen = Boolean(treatmentsAnchor);

  const isRouteActive = useMemo(() => {
    const pathname = location.pathname || "/";
    return (to) => {
      if (to === "/") return pathname === "/";
      return pathname === to || pathname.startsWith(`${to}/`);
    };
  }, [location.pathname]);

  const isTreatmentsActive = location.pathname.startsWith("/treatments");

  const activeStyle = {
    color: "#b39800",
  };

  const inactiveStyle = {
    color: "#3d3530",
  };

  const ctaStyle = {
    backgroundColor: "#5a4d47",
    color: "#fff",
    padding: "10px 20px",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: 600,
    letterSpacing: "0.3px",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "#7a6b63",
      color: "#fff",
    },
  };

  const closeTreatmentsMenu = () => setTreatmentsAnchor(null);

  const renderDesktopNavItem = (item) => {
    if (item.children) {
      return (
        <Box key={item.label} sx={{ display: "flex", alignItems: "center" }}>
          <Button
            onClick={(e) => {
              setTreatmentsAnchor(e.currentTarget);
              trackNavClick("Treatments", "Navbar Desktop Dropdown");
            }}
            endIcon={<KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />}
            aria-haspopup="true"
            aria-expanded={treatmentsMenuOpen}
            sx={{
              ...(isTreatmentsActive ? activeStyle : inactiveStyle),
              textTransform: "none",
              fontSize: "14px",
              lineHeight: 1.7,
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 500,
              p: 0,
              minWidth: 0,
              letterSpacing: "0.3px",
              "&:hover": { backgroundColor: "transparent", color: "#b39800" },
            }}
          >
            {item.label}
          </Button>
          <Menu
            anchorEl={treatmentsAnchor}
            open={treatmentsMenuOpen}
            onClose={closeTreatmentsMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: "8px",
                boxShadow: "0 4px 20px rgba(90, 77, 71, 0.12)",
              },
            }}
          >
            {item.children.map(({ to, label }) => (
              <MenuItem
                key={to}
                component={NavLink}
                to={to}
                onClick={() => {
                  closeTreatmentsMenu();
                  trackNavClick(label, "Navbar Desktop Dropdown");
                }}
                sx={{
                  color: "#3d3530",
                  fontSize: "14px",
                  fontFamily: "'Montserrat', sans-serif",
                  py: 1.2,
                  "&:hover": { color: "#b39800", backgroundColor: "#faf8f5" },
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      );
    }

    const isExternal = /^https?:\/\//i.test(item.to);

    if (isExternal) {
      return (
        <Link
          key={item.to}
          href={item.to}
          underline="none"
          sx={item.isCta ? ctaStyle : inactiveStyle}
          target="_blank"
          rel="noreferrer"
          onClick={item.isCta ? () => trackBookingClick("Navbar Desktop") : undefined}
        >
          {item.label}
        </Link>
      );
    }

    return (
      <Link
        key={item.to}
        component={NavLink}
        to={item.to}
        underline="none"
        onClick={() => trackNavClick(item.label, "Navbar Desktop")}
        sx={{
          ...(isRouteActive(item.to) ? activeStyle : inactiveStyle),
          fontSize: "14px",
          fontWeight: 500,
          "&:hover": { color: "#b39800" },
        }}
      >
        {item.label}
      </Link>
    );
  };

  const renderMobileNavItem = (item) => {
    if (item.children) {
      return (
        <Box key={item.label}>
          <ListItemButton
            onClick={() => {
              setTreatmentsOpenMobile((prev) => {
                if (!prev) trackNavClick("Treatments", "Navbar Mobile Dropdown");
                return !prev;
              });
            }}
            sx={{
              borderRadius: 2,
              ...(isTreatmentsActive && {
                backgroundColor: "rgba(179, 152, 0, 0.08)",
              }),
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                sx: {
                  color: isTreatmentsActive ? "#b39800" : "#3d3530",
                  fontWeight: isTreatmentsActive ? 600 : 500,
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: "15px",
                },
              }}
            />
            {treatmentsOpenMobile ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={treatmentsOpenMobile} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map(({ to, label }) => (
                <ListItemButton
                  key={to}
                  component={NavLink}
                  to={to}
                  onClick={() => {
                    setDrawerOpen(false);
                    trackNavClick(label, "Navbar Mobile Dropdown");
                  }}
                  sx={{ borderRadius: 2, pl: 4 }}
                >
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      sx: {
                        color: "#3d3530",
                        fontWeight: 400,
                        fontFamily: "'Montserrat', sans-serif",
                        fontSize: "14px",
                      },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          </Collapse>
        </Box>
      );
    }

    const isExternal = /^https?:\/\//i.test(item.to);

    if (isExternal) {
      return (
        <ListItemButton
          key={item.to}
          component="a"
          href={item.to}
          target="_blank"
          rel="noreferrer"
          onClick={() => {
            setDrawerOpen(false);
            if (item.isCta) trackBookingClick("Navbar Mobile");
          }}
          sx={{
            borderRadius: 2,
            ...(item.isCta && {
              mt: 2,
              backgroundColor: "#5a4d47",
              "&:hover": { backgroundColor: "#7a6b63" },
            }),
          }}
        >
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              sx: {
                color: item.isCta ? "#fff" : "#3d3530",
                fontWeight: item.isCta ? 600 : 500,
                fontFamily: "'Montserrat', sans-serif",
                fontSize: "15px",
                textAlign: item.isCta ? "center" : "left",
              },
            }}
          />
        </ListItemButton>
      );
    }

    return (
      <ListItemButton
        key={item.to}
        component={NavLink}
        to={item.to}
        selected={isRouteActive(item.to)}
        onClick={() => {
          setDrawerOpen(false);
          trackNavClick(item.label, "Navbar Mobile");
        }}
        sx={{
          borderRadius: 2,
          "&.Mui-selected": {
            backgroundColor: "rgba(179, 152, 0, 0.08)",
          },
          "&.Mui-selected:hover": {
            backgroundColor: "rgba(179, 152, 0, 0.12)",
          },
        }}
      >
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            sx: {
              color: isRouteActive(item.to) ? "#b39800" : "#3d3530",
              fontWeight: isRouteActive(item.to) ? 600 : 500,
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "15px",
            },
          }}
        />
      </ListItemButton>
    );
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: "#fff",
        boxShadow: "0 2px 12px rgba(90, 77, 71, 0.08)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        sx={{
          maxWidth: "1108px",
          margin: "0 auto",
          px: { xs: 2, sm: 4 },
          py: 2,
          justifyContent: "space-between",
        }}
      >
        <Link
          component={NavLink}
          to="/"
          underline="none"
          aria-label="Go to home"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={1.5}
            sx={{
              "& img": {
                width: { xs: "36px", sm: "44px" },
                height: { xs: "36px", sm: "44px" },
              },
            }}
          >
            <Logo />
            <Typography
              sx={{
                color: "#5a4d47",
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontSize: { xs: "18px", sm: "22px" },
                lineHeight: 1.2,
                display: { xs: "none", md: "block" },
              }}
            >
              Radiance Aesthetics
            </Typography>
          </Stack>
        </Link>
        <Stack
          direction="row"
          alignItems="center"
          gap={3}
          sx={{ display: { xs: "none", sm: "flex" } }}
        >
          {navLinks.map(renderDesktopNavItem)}
        </Stack>
        <IconButton
          aria-label="Open menu"
          onClick={() => {
            setDrawerOpen(true);
            trackNavClick("Mobile Menu", "Navbar Mobile");
          }}
          sx={{ display: { xs: "inline-flex", sm: "none" } }}
        >
          <MenuIcon sx={{ color: "#5a4d47" }} />
        </IconButton>
      </Stack>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box
          sx={{ width: 300 }}
          role="presentation"
          onKeyDown={(e) => {
            if (e.key === "Escape") setDrawerOpen(false);
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2.5, py: 2 }}
          >
            <Typography
              sx={{
                color: "#5a4d47",
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 500,
                fontSize: "20px",
              }}
            >
              Radiance Aesthetics
            </Typography>
            <IconButton
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <List sx={{ px: 1.5, py: 1 }}>
            {navLinks.map(renderMobileNavItem)}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default NavBar;
